import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from '../../hooks/useForm';
import { useTranslation } from 'react-i18next';
import { crearErrorAction } from '../../actions/ui';
import {
  addFormularioCat1, addProgramaCat1,
  addFormularioCat2, addProgramaCat2,
  addFormularioCat3, addProgramaCat3,
  addFormularioCat4, addProgramaCat4,
  addFormularioCat5, addProgramaCat5,
  addFormularioCat6, addProgramaCat6,
  addFormularioCat7, addProgramaCat7,
  addFormularioCat8, addProgramaCat8,
  addFormularioCat9, addProgramaCat9,
} from '../../actions/configFormularioAction';

// ── Mapa de actions por categoría ─────────────────────────────────────────────
const ACTION_MAP = {
  1: { cat: addFormularioCat1, prog: addProgramaCat1 },
  2: { cat: addFormularioCat2, prog: addProgramaCat2 },
  3: { cat: addFormularioCat3, prog: addProgramaCat3 },
  4: { cat: addFormularioCat4, prog: addProgramaCat4 },
  5: { cat: addFormularioCat5, prog: addProgramaCat5 },
  6: { cat: addFormularioCat6, prog: addProgramaCat6 },
  7: { cat: addFormularioCat7, prog: addProgramaCat7 },
  8: { cat: addFormularioCat8, prog: addProgramaCat8 },
  9: { cat: addFormularioCat9, prog: addProgramaCat9 },
};

// ── Helpers para construir preguntas desde el state ───────────────────────────
const buildQuestions = (catData, catNum) => {
  if (!catData) return [];
  return Object.entries(catData)
    .filter(([key]) => key.startsWith(`Cat${catNum}_Preg`))
    .map(([key, value]) => {
      // Detectar si hay desplegable asociado a esta pregunta
      const pregNum = key.match(/Preg(\d+)/)?.[1];
      const desplegableKey = `Cat${catNum}_Desplegable${pregNum}`;
      const desplegable = Object.entries(catData).filter(([dKey]) =>
        new RegExp(`^Cat${catNum}_Desplegable${pregNum}(?:$|_)`).test(dKey)
      );
      const options = desplegable.length > 0
        ? desplegable.map(([dKey, dValue]) => ({ value: dKey.split('_').pop(), label: dValue })).reverse()
        : [];
      if (options.length > 0) options.unshift({ value: '', label: '— Seleccionar —' });
      return {
        id: key,
        label: value,
        type: desplegable.length > 0 ? 'select' : 'text',
        options,
      };
    });
};

const buildProgramQuestions = (catData, catNum, desplegableTexts) => {
  if (!catData || !desplegableTexts) return [];
  return Object.entries(catData)
    .filter(([key]) => key.startsWith(`Cat${catNum}_Prog`))
    .map(([key, value]) => ({
      id: key,
      label: value,
      type: 'select',
      options: [
        { value: '',  label: desplegableTexts.CatProg_Desplegable_Seleccionar },
        { value: '2', label: desplegableTexts.CatProg_Desplegable_Si },
        { value: '1', label: desplegableTexts.CatProg_Desplegable_Mitat },
        { value: '0', label: desplegableTexts.CatProg_Desplegable_No },
      ],
    }));
};

// ── Componente genérico ───────────────────────────────────────────────────────
export const FormularioCat = ({ catNum }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation('generic');
  const { auth, configFormulario, ui } = useSelector((s) => s);
  const { msgError } = ui;
  const textCat = configFormulario.infoFormularioTextCategoria;
  const catData = textCat?.[`Categoria${catNum}`];
  const desplegableTexts = textCat?.CategoriaDesplegableProg;
  const resultadoCat  = configFormulario.infoFormularioCategoria?.[`ResultatCat${catNum}`];
  const IdEvaluacion  = configFormulario.infoFormulario?.IdEvaluacion;

  const [formValues, handleInputChange, reset] = useForm({});

  const questions      = buildQuestions(catData, catNum);
  const programQuestions = buildProgramQuestions(catData, catNum, desplegableTexts);

  const validar = (qs, label) => {
    let ok = true;
    let msg = '';
    qs.forEach((q, i) => {
      const v = formValues[q.id];
      if (!v || (q.type === 'text' && isNaN(v))) {
        ok = false;
        msg += `${t('formulario.msgError1')}${i + 1} ${t('formulario.msgError2')}\n`;
      }
    });
    if (!ok) dispatch(crearErrorAction(msg));
    return ok;
  };

  const handleEnviarCat = () => {
    if (!validar(questions)) return;
    const payload = questions.map((q) => formValues[q.id]);
    dispatch(ACTION_MAP[catNum].cat(auth.tokenJwt, ...payload, IdEvaluacion));
    reset();
  };

  const handleEnviarPrograma = () => {
    if (!validar(programQuestions)) return;
    const payload = programQuestions.map((q) => formValues[q.id]);
    dispatch(ACTION_MAP[catNum].prog(auth.tokenJwt, ...payload, IdEvaluacion));
  };

  const activeQuestions = resultadoCat == null ? questions : programQuestions;
  const handleEnviar    = resultadoCat == null ? handleEnviarCat : handleEnviarPrograma;
  const fase            = resultadoCat == null ? 'indicadores' : 'programas';

  return (
    <div className="fp-cat-panel">

      {/* Fase badge */}
      <div className="fp-fase-badge-wrap">
        <span className={`fp-fase-badge ${fase === 'programas' ? 'fp-fase-badge--prog' : ''}`}>
          {fase === 'indicadores' ? '📊 Preguntas' : '📋 Programas'}
        </span>
        {resultadoCat != null && (
          <span className="fp-fase-resultado">
            Resultado indicadores: <strong>{resultadoCat}</strong>
          </span>
        )}
      </div>

      {msgError && (
        <div className="ms-alert-error" style={{ marginBottom: 16 }}>{msgError}</div>
      )}

      {/* Preguntas */}
      <div className="fp-preguntas">
        {activeQuestions.map((q, idx) => (
          <PreguntaField
            key={q.id}
            num={idx + 1}
            question={q}
            value={formValues[q.id] || ''}
            onChange={handleInputChange}
          />
        ))}
      </div>

      {/* Botón enviar */}
      <div className="fp-cat-footer">
        <button onClick={handleEnviar} className="u-btn-primary">
          {t('formulario.enviar')}
        </button>
      </div>
    </div>
  );
};

// ── Campo de pregunta rediseñado ──────────────────────────────────────────────
const PreguntaField = ({ num, question, value, onChange }) => {
  const hasIndent = question.type === 'select';
  return (
  <div className={`fp-pregunta${hasIndent ? ' fp-pregunta--indent' : ''}`}>
    <label className="fp-pregunta-label" htmlFor={question.id}>
      <span className="fp-pregunta-num">{num}</span>
      {question.label}
    </label>
    {question.type === 'select' ? (
      <select
        id={question.id}
        name={question.id}
        value={value}
        onChange={onChange}
        className="fp-select"
      >
        {question.options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    ) : (
      <input
        id={question.id}
        type="number"
        name={question.id}
        value={value}
        onChange={onChange}
        className="fp-input"
        placeholder="0"
      />
    )}
  </div>

);
};